// src/pages/ProductDetailPage.jsx - 修正版
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, Heart, Share2, ShoppingCart, Minus, Plus, Truck, Shield, RotateCcw, MessageCircle, ThumbsUp } from 'lucide-react';
import { useCart } from './context/CartContext';
import { productAPI } from './services/api';
import Header from './components/Header'; 

const ProductDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToCart, loading: cartLoading } = useCart();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedStorage, setSelectedStorage] = useState('');
  const [activeTab, setActiveTab] = useState('description');

  // ✅ 從 API 載入產品數據
  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      try {
        console.log('載入產品:', id);
        
        // ✅ 使用真實 API
        const productData = await productAPI.getProductById(parseInt(id));
        
        if (!productData) {
          alert(t('errors.productNotFound') || '產品不存在');
          navigate('/products');
          return;
        }
        
        console.log('產品數據:', productData);
        setProduct(productData);
        
        // ✅ 載入相關產品
        const related = await productAPI.getRelatedProducts(id, 4);
        console.log('相關產品:', related);
        setRelatedProducts(related);
        
      } catch (error) {
        console.error('載入產品失敗:', error);
        alert(t('errors.loadProductFailed') || '載入產品失敗');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    
    loadProduct();
  }, [id, navigate, t]);

  // 當產品載入後，設置默認選項
  useEffect(() => {
    if (product) {
      // 處理 colors
      if (product.colors && product.colors.length > 0) {
        const firstColor = product.colors[0];
        const colorName = typeof firstColor === 'object' ? (firstColor.name || firstColor.color || '') : firstColor;
        setSelectedColor(colorName);
      } else {
        setSelectedColor('');
      }
      
      // 處理 storage
      if (product.storage && product.storage.length > 0) {
        const firstStorage = product.storage[0];
        const storageText = typeof firstStorage === 'object' ? JSON.stringify(firstStorage) : String(firstStorage);
        setSelectedStorage(storageText);
      } else {
        setSelectedStorage('');
      }
      
      setSelectedImage(0);
      setQuantity(1);
    }
  }, [product]);

  // 數量控制
  const increaseQty = () => {
    if (quantity < (product?.stock || 99)) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQty = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  // ✅ 處理加入購物車
  const handleAddToCart = async () => {
    try {
      const currentImageUrl = getCurrentImageUrl();
    
      const productData = {
        productId: product.id,
        productName: product.name,
        variant: `${selectedColor} ${selectedStorage}`.trim() || '默認規格',
        price: product.price,
        originalPrice: product.original_price || product.price,
        quantity: quantity,
        stock: product.stock,
        imageUrl: currentImageUrl,
        sellerName: product.seller?.name || product.seller_name || '官方旗艦店'
      };

      console.log('加入購物車:', productData);
      
      await addToCart(productData);
      
      alert(t('success.addedToCart') || '✅ 已加入購物車!');
    } catch (error) {
      console.error('加入購物車失敗:', error);
      alert(`❌ ${error.message || t('errors.addToCartFailed')}`);
    }
  };

  // ✅ 立即購買
  const handleBuyNow = async () => {
    await handleAddToCart();
    navigate('/cart');
  };

  // ✅ 獲取當前顯示的圖片 URL
  const getCurrentImageUrl = () => {
    const displayImages = getDisplayImages();
    return displayImages[selectedImage] || '';
  };

  // ✅ 獲取顯示的圖片列表（優先 base64）
  const getDisplayImages = () => {
    if (!product) return [];
    
    const images = [];
    
    // ✅ 如果有 images 數組（對象數組格式：{url, order, isMain}）
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images.map(img => {
        // 如果是對象，提取url屬性
        if (typeof img === 'object' && img.url) {
          return img.url;
        }
        // 如果是字符串，直接返回
        return img;
      });
    }
    
    // ✅ 如果有單個 image 欄位
    if (product.image) {
      images.push(product.image);
    }
    
    return images.length > 0 ? images : ['🎨'];
  };

  // ✅ 渲染產品圖片（支持 base64）
  const renderProductImage = (imageData, isMain = false) => {
    // 如果是 base64 或 URL
    if (typeof imageData === 'string' && (imageData.startsWith('data:image') || imageData.startsWith('http'))) {
      return (
        <img 
          src={imageData} 
          alt={product?.name || '產品圖片'}
          className="w-full h-full object-cover"
        />
      );
    }
    
    // 如果是 emoji 或其他
    const size = isMain ? 'text-9xl' : 'text-4xl';
    return <span className={size}>{imageData}</span>;
  };

  // 載入中狀態
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">📦</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('common.loading') || '載入中...'}
          </h2>
          <p className="text-gray-600">
            {t('productDetail.loadingProduct') || '正在載入產品資訊'}
          </p>
        </div>
      </div>
    );
  }

  // 產品不存在
  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😞</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('errors.productNotFound') || '產品不存在'}
          </h2>
          <p className="text-gray-600 mb-4">
            {t('errors.productNotFoundDesc') || '抱歉，找不到您要查看的產品'}
          </p>
          <button 
            onClick={() => navigate('/products')}
            className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600"
          >
            {t('productDetail.backToProducts') || '返回產品列表'}
          </button>
        </div>
      </div>
    );
  }

  const displayImages = getDisplayImages();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ✅ 頂部導航 - 使用 product variant */}
      <Header variant="product" />

      {/* 麵包屑 */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-orange-500">
              {t('common.home') || '首頁'}
            </button>
            <span>/</span>
            <button onClick={() => navigate('/products')} className="hover:text-orange-500">
              {product.category}
            </button>
            <span>/</span>
            <span className="text-gray-900">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* 產品主要信息 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* ✅ 左側：產品圖片 */}
            <div>
              {/* 主圖片 */}
              <div className="aspect-square bg-gray-100 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                {renderProductImage(displayImages[selectedImage], true)}
              </div>
              
              {/* ✅ 縮略圖列表 */}
              {displayImages.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {displayImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border-2 ${
                        selectedImage === index ? 'border-orange-500' : 'border-transparent'
                      }`}
                    >
                      {renderProductImage(img, false)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 右側：產品信息 */}
            <div>
              {/* 產品名稱 */}
              <h1 className="text-3xl font-bold mb-4">{product.name}</h1>
              
              {/* 評分 */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={20}
                      className={i < Math.floor(product.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                    />
                  ))}
                </div>
                <span className="text-gray-600">
                  {product.rating || 0} {t('productDetail.rating') || '評分'}
                </span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-600">
                  {product.reviews_count || 0} {t('productDetail.reviews_count') || '則評價'}
                </span>
              </div>

              {/* 價格 */}
              <div className="mb-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-4xl font-bold text-orange-500">
                    TWD {product.price}
                  </span>
                  {product.original_price && product.original_price > product.price && (
                    <>
                      <span className="text-xl text-gray-400 line-through">
                        TWD {product.original_price}
                      </span>
                      <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-sm font-medium">
                        {t('productDetail.youSave') || '您節省了'} TWD {product.original_price - product.price}
                      </span>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {t('productDetail.taxIncluded') || '含稅'}
                </p>
              </div>

              {/* ✅ 顏色選擇 */}
              {product.colors && product.colors.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">
                    {t('productDetail.selectColor') || '選擇顏色'}
                  </label>
                  <div className="flex gap-2">
                    {product.colors.map((color, index) => {
                      const colorName = typeof color === 'object' ? (color.name || color.color || `顏色${index + 1}`) : color;
                      return (
                        <button
                          key={colorName}
                          onClick={() => setSelectedColor(colorName)}
                          className={`px-4 py-2 border-2 rounded-lg transition ${
                            selectedColor === colorName
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {colorName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ✅ 容量/規格選擇 */}
              {product.storage && product.storage.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">
                    {t('productDetail.variant') || '規格'}
                  </label>
                  <div className="flex gap-2">
                    {product.storage.map((storage, index) => {
                      const storageText = typeof storage === 'object' ? JSON.stringify(storage) : String(storage);
                      return (
                        <button
                          key={index}
                          onClick={() => setSelectedStorage(storageText)}
                          className={`px-4 py-2 border-2 rounded-lg transition ${
                            selectedStorage === storageText
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {storageText}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 數量選擇 */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  {t('productDetail.quantity') || '數量'}
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={decreaseQty}
                    className="w-10 h-10 border rounded-lg flex items-center justify-center hover:bg-gray-100"
                  >
                    <Minus size={20} />
                  </button>
                  <span className="w-16 text-center text-lg font-medium">{quantity}</span>
                  <button
                    onClick={increaseQty}
                    className="w-10 h-10 border rounded-lg flex items-center justify-center hover:bg-gray-100"
                  >
                    <Plus size={20} />
                  </button>
                  <span className="text-sm text-gray-500">
                    {t('productDetail.inStock') || '庫存'}: {product.stock}
                  </span>
                </div>
              </div>

              {/* 操作按鈕 */}
              <div className="flex gap-3 mb-6">
                <button
                  onClick={handleAddToCart}
                  disabled={cartLoading || product.stock === 0}
                  className="flex-1 bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={20} />
                  {t('productDetail.addToCart') || '加入購物車'}
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={cartLoading || product.stock === 0}
                  className="flex-1 bg-red-500 text-white py-3 rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {t('productDetail.buyNow') || '立即購買'}
                </button>
              </div>

              {/* 收藏和分享 */}
              <div className="flex gap-3 mb-6">
                <button className="flex-1 border-2 border-gray-300 py-2 rounded-lg hover:border-orange-500 hover:text-orange-500 flex items-center justify-center gap-2">
                  <Heart size={20} />
                  {t('common.favorite') || '收藏'}
                </button>
                <button className="flex-1 border-2 border-gray-300 py-2 rounded-lg hover:border-orange-500 hover:text-orange-500 flex items-center justify-center gap-2">
                  <Share2 size={20} />
                  {t('common.share') || '分享'}
                </button>
              </div>

              {/* ✅ 保證信息 */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Truck className="text-orange-500" size={24} />
                  <div>
                    <div className="font-medium text-sm">
                      {t('productDetail.freeShipping') || '免運配送'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t('productDetail.deliveryTime') || '3-5個工作天'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="text-orange-500" size={24} />
                  <div>
                    <div className="font-medium text-sm">
                      {t('productDetail.authenticGuarantee') || '正品保證'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t('productDetail.guarantee') || '假一賠十'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <RotateCcw className="text-orange-500" size={24} />
                  <div>
                    <div className="font-medium text-sm">
                      {t('productDetail.returnPolicy') || '7天退貨'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t('productDetail.warranty') || '無理由退換'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ 產品詳情標籤區 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          {/* 標籤切換 */}
          <div className="flex border-b mb-6">
            <button
              onClick={() => setActiveTab('description')}
              className={`px-6 py-3 font-medium transition ${
                activeTab === 'description'
                  ? 'text-orange-500 border-b-2 border-orange-500'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('productDetail.description') || '產品描述'}
            </button>
            <button
              onClick={() => setActiveTab('specs')}
              className={`px-6 py-3 font-medium transition ${
                activeTab === 'specs'
                  ? 'text-orange-500 border-b-2 border-orange-500'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('productDetail.specifications') || '規格參數'}
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`px-6 py-3 font-medium transition ${
                activeTab === 'reviews'
                  ? 'text-orange-500 border-b-2 border-orange-500'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('productDetail.reviews') || '顧客評價'} ({product.reviews_count || 0})
            </button>
          </div>

          {/* 標籤內容 */}
          <div>
            {activeTab === 'description' && (
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {product.description || t('productDetail.noDescription') || '暫無產品描述'}
                </p>
                
                {/* ✅ 顯示產品特點 */}
                {product.features && product.features.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-xl font-bold mb-4">
                      {t('productDetail.features') || '主要特點'}
                    </h3>
                    <ul className="space-y-2">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-orange-500 mt-1">✓</span>
                          <span>
                            {typeof feature === 'object' ? JSON.stringify(feature) : feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'specs' && (
              <div className="grid grid-cols-2 gap-4">
                {product.specifications && Object.keys(product.specifications).length > 0 ? (
                  Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex border-b pb-2">
                      <span className="text-gray-600 w-32">{key}</span>
                      <span className="text-gray-900 font-medium">
                        {typeof value === 'object' ? JSON.stringify(value) : value}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 col-span-2">
                    {t('productDetail.noSpecs') || '暫無規格參數'}
                  </p>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-4">
                {/* 評價統計 */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <div className="text-5xl font-bold text-orange-500">
                        {product.rating || 0}
                      </div>
                      <div className="flex mt-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={20}
                            className={i < Math.floor(product.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                          />
                        ))}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {product.reviews_count || 0} {t('productDetail.reviews_count') || '則評價'}
                      </div>
                    </div>
                    <div className="flex-1">
                      {[5, 4, 3, 2, 1].map((star) => (
                        <div key={star} className="flex items-center gap-2 mb-1">
                          <span className="text-sm w-12">{star} ⭐</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-orange-500 h-2 rounded-full"
                              style={{ width: `${Math.random() * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 評價列表 - 這裡可以之後從 API 獲取真實評價 */}
                <div className="space-y-4">
                  <p className="text-gray-500 text-center py-8">
                    暫無評價，成為第一個評價的人吧！
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ✅ 相關商品推薦 */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-4">
              {t('productDetail.relatedProducts') || '相關推薦'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map((item) => (
                <div
                  key={item.id}
                  onClick={() => navigate(`/product/${item.id}`)}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition cursor-pointer overflow-hidden"
                >
                  <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                    {item.image && item.image.startsWith('data:image') ? (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-5xl">{item.image || '🎨'}</span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-sm mb-2 line-clamp-2 h-10">
                      {item.name}
                    </h3>
                    <div className="flex items-center gap-1 mb-2">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm">{item.rating || 0}</span>
                      <span className="text-xs text-gray-500">
                        ({item.reviews_count || 0})
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-orange-500">
                        TWD {item.price}
                      </span>
                      {item.original_price && item.original_price > item.price && (
                        <span className="text-sm text-gray-400 line-through">
                          TWD {item.original_price}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;